/**
 * Vertical drawing toolbar.
 *
 * Six groups, as TradingView lays them out, because eighty-six tools do not
 * fit in a column beside the chart. Each group shows the tool last chosen
 * from it and opens the rest on click -- so the bar is the length of the
 * groups, not of the catalogue.
 */
import type { DrawingKind } from "lightweight-charts-drawing";
import { useEffect, useRef, useState } from "react";

import { TOOL_GROUPS, type ToolEntry } from "./drawingTools";
import { ToolIcon } from "./ToolIcon";

/** One icon per group, in the order TOOL_GROUPS declares them. */
const GROUP_ICON = ["trend", "fib", "pattern", "ruler", "rect", "text"];

export function LeftToolbar({
  tool, onTool, magnet, onMagnet, stay, onStay, count, onClear, profile,
}: {
  tool: DrawingKind | null;
  onTool: (t: DrawingKind | null) => void;
  magnet: boolean;
  onMagnet: (v: boolean) => void;
  stay: boolean;
  onStay: (v: boolean) => void;
  count: number;
  onClear: () => void;
  /** Rendered at the foot of the rail, pushed there by the spacer. */
  profile?: React.ReactNode;
}) {
  const [open, setOpen] = useState<number | null>(null);
  // The tool each group shows on its button: the last one picked from it,
  // like TradingView. Starting from the first of each group means the bar is
  // never blank before a choice has been made.
  const [recent, setRecent] = useState<string[]>(
    () => TOOL_GROUPS.map((g) => g.sections[0]?.tools[0]?.kind ?? ""),
  );
  const barRef = useRef<HTMLDivElement>(null);

  // A click anywhere else closes the flyout. Without this it stays open over
  // the chart and swallows the click meant for the drawing.
  useEffect(() => {
    if (open === null) return;
    const away = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const pick = (gi: number, t: ToolEntry) => {
    setRecent((r) => r.map((k, i) => (i === gi ? t.kind : k)));
    onTool(t.kind as DrawingKind);
    setOpen(null);
  };

  return (
    <div className="toolbar" ref={barRef}>
      <button
        className={`tool ${tool === null ? "active" : ""}`}
        onClick={() => { onTool(null); setOpen(null); }}
        title="Cursor — pan and select (Esc)"
        aria-pressed={tool === null}
      >
        <ToolIcon name="cursor" />
      </button>

      {TOOL_GROUPS.map((group, gi) => {
        const current = recent[gi] ?? "";
        const inGroup = group.sections.some((s) => s.tools.some((t) => t.kind === tool));
        return (
          <div className="tool-group" key={group.title}>
            <button
              className={`tool ${inGroup ? "active" : ""}`}
              title={`${group.title} — click to choose`}
              aria-haspopup="menu"
              aria-expanded={open === gi}
              onClick={() => {
                // One click arms the group's current tool and opens the list,
                // so the common case needs no second click.
                onTool(current as DrawingKind);
                setOpen(open === gi ? null : gi);
              }}
            >
              <ToolIcon name={GROUP_ICON[gi] ?? "trend"} />
              <i className="tool-caret" />
            </button>

            {open === gi && (
              <div className="tool-flyout" role="menu">
                <div className="tf-title">{group.title}</div>
                {group.sections.map((section) => (
                  <div key={section.header ?? "_"}>
                    {section.header && <div className="tf-head">{section.header}</div>}
                    {section.tools.map((t) => (
                      <button
                        key={t.kind}
                        role="menuitem"
                        className={`tf-item ${tool === t.kind ? "on" : ""}`}
                        onClick={() => pick(gi, t)}
                      >
                        <span>{t.title}</span>
                        <em>{t.placement}</em>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="tool-sep" />

      <button
        className={`tool ${magnet ? "active" : ""}`}
        onClick={() => onMagnet(!magnet)}
        title="Magnet — snap new points to the nearest open, high, low or close"
        aria-pressed={magnet}
      >
        <ToolIcon name="magnet" />
      </button>
      <button
        className={`tool ${stay ? "active" : ""}`}
        onClick={() => onStay(!stay)}
        title="Keep drawing — stay on the tool after placing one"
        aria-pressed={stay}
      >
        <ToolIcon name="pencil" />
      </button>
      <button
        className="tool"
        onClick={onClear}
        disabled={count === 0}
        title={count ? `Remove all ${count} drawings on this symbol` : "Nothing to remove"}
      >
        <ToolIcon name="trash" />
      </button>
      {count > 0 && <div className="tool-count">{count}</div>}

      {/* Everything above is tools; this pushes the account to the bottom. */}
      <div className="tool-fill" />
      {profile}
    </div>
  );
}
